import ExcelJS from "exceljs";

const PLATFORM_NAME = process.env.REACT_APP_PROJECT_NAME || "AGCX";
const BRAND_NAME = "Arab Global Crypto Exchange";

/** AGCX brand palette */
const GOLD = "D1AA67";
const GOLD_SOFT = "F8F1E3";
const GOLD_BAND = "EFE0C0";
const INK = "1E2329";
const MUTED = "707A8A";
const HEADER_BG = "1E2329";
const HEADER_FG = "FFFFFF";
const ROW_WHITE = "FFFFFF";
const ALT_ROW = "F7F4EC";
const BORDER = "E6E8EB";
const SUCCESS = "0ECB81";
const SUCCESS_BG = "E8FBF3";
const DANGER = "F6465D";
const DANGER_BG = "FDECEF";
const WARN = "F0B90B";
const WARN_BG = "FFF8E6";

async function loadLogoAsBase64Png(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const max = 220;
        const scale = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
        canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve({
          dataUrl: canvas.toDataURL("image/png"),
          width: canvas.width,
          height: canvas.height,
        });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

async function resolveBrandLogo() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const candidates = [
    `${origin}/images/logo_light.svg`,
    `${origin}/images/favicon_light.svg`,
    `${origin}/logo192.png`,
    `${origin}/images/logo.png`,
  ];
  for (const url of candidates) {
    // eslint-disable-next-line no-await-in-loop
    const logo = await loadLogoAsBase64Png(url);
    if (logo?.dataUrl) return logo;
  }
  return null;
}

function dataUrlToArrayBuffer(dataUrl) {
  const base64 = String(dataUrl || "").split(",")[1] || "";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function thinBorder() {
  return {
    top: { style: "thin", color: { argb: `FF${BORDER}` } },
    left: { style: "thin", color: { argb: `FF${BORDER}` } },
    bottom: { style: "thin", color: { argb: `FF${BORDER}` } },
    right: { style: "thin", color: { argb: `FF${BORDER}` } },
  };
}

function solidFill(hex) {
  return { type: "pattern", pattern: "solid", fgColor: { argb: `FF${hex}` } };
}

function paintRow(sheet, rowIndex, colCount, hex) {
  const row = sheet.getRow(rowIndex);
  for (let c = 1; c <= colCount; c += 1) {
    row.getCell(c).fill = solidFill(hex);
  }
}

function sideTone(row) {
  const side = String(row.side || row.sideLabel || "").toUpperCase();
  if (side === "BUY" || side.includes("BUY")) return { fg: SUCCESS, bg: SUCCESS_BG };
  if (side === "SELL" || side.includes("SELL")) return { fg: DANGER, bg: DANGER_BG };
  return { fg: INK, bg: null };
}

function statusTone(row) {
  const status = String(row.status || row.statusLabel || "").toUpperCase();
  if (
    status.includes("EXECUTED") ||
    status.includes("SUCCESS") ||
    status.includes("COMPLETED") ||
    status.includes("FILLED")
  ) {
    return { fg: SUCCESS, bg: SUCCESS_BG };
  }
  if (status.includes("FAIL") || status.includes("REJECT") || status.includes("CANCEL")) {
    return { fg: DANGER, bg: DANGER_BG };
  }
  if (status.includes("PENDING") || status.includes("QUOTE") || status.includes("PROCESS")) {
    return { fg: WARN, bg: WARN_BG };
  }
  return { fg: INK, bg: null };
}

/**
 * Branded Convert History Excel for Buy/Sell.
 * @param {Array<object>} rows mapped convert history rows
 * @param {{ timeLabel?: string, statusLabel?: string }} meta
 */
export async function exportConvertHistoryBrandedExcel(rows, meta = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const timeLabel = meta.timeLabel || "All time";
  const statusLabel = meta.statusLabel || "All status";
  const exportedAt = new Date().toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

  const workbook = new ExcelJS.Workbook();
  workbook.creator = PLATFORM_NAME;
  workbook.created = new Date();
  workbook.company = BRAND_NAME;

  const colCount = 8;
  const sheet = workbook.addWorksheet("Convert History", {
    views: [{ state: "frozen", ySplit: 9, showGridLines: false }],
    properties: { defaultRowHeight: 20 },
    pageSetup: {
      orientation: "landscape",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      paperSize: 9,
      margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 },
    },
  });

  sheet.columns = [
    { key: "date", width: 22 },
    { key: "side", width: 11 },
    { key: "spend", width: 16 },
    { key: "receive", width: 18 },
    { key: "fee", width: 18 },
    { key: "wallet", width: 11 },
    { key: "status", width: 13 },
    { key: "ref", width: 32 },
  ];

  // —— Top gold accent ——
  const accent = sheet.getRow(1);
  accent.height = 10;
  paintRow(sheet, 1, colCount, GOLD);

  // —— Header band (rows 2–7) ——
  for (let r = 2; r <= 7; r += 1) paintRow(sheet, r, colCount, GOLD_SOFT);

  // Logo + brand (logo in col A, text from col B)
  let logoPlaced = false;
  try {
    const logo = await resolveBrandLogo();
    if (logo?.dataUrl) {
      const imageId = workbook.addImage({
        buffer: dataUrlToArrayBuffer(logo.dataUrl),
        extension: "png",
      });
      const aspect = logo.height > 0 ? logo.width / logo.height : 2;
      const logoH = 42;
      const logoW = Math.min(110, Math.round(logoH * aspect));
      sheet.addImage(imageId, {
        tl: { col: 0.2, row: 1.35 },
        ext: { width: logoW, height: logoH },
      });
      logoPlaced = true;
    }
  } catch {
    /* optional */
  }

  // Brand name sits in B–H so it never overlaps the logo cell
  const brandStartCol = logoPlaced ? 2 : 1;
  sheet.mergeCells(2, brandStartCol, 2, colCount);
  const brandRow = sheet.getRow(2);
  brandRow.height = 26;
  brandRow.getCell(brandStartCol).value = BRAND_NAME;
  brandRow.getCell(brandStartCol).font = {
    name: "Calibri",
    size: 18,
    bold: true,
    color: { argb: `FF${INK}` },
  };
  brandRow.getCell(brandStartCol).alignment = { vertical: "middle", horizontal: "left" };

  sheet.mergeCells(3, brandStartCol, 3, colCount);
  const codeRow = sheet.getRow(3);
  codeRow.height = 18;
  codeRow.getCell(brandStartCol).value = PLATFORM_NAME;
  codeRow.getCell(brandStartCol).font = {
    name: "Calibri",
    size: 11,
    bold: true,
    color: { argb: `FF${GOLD}` },
  };
  codeRow.getCell(brandStartCol).alignment = { vertical: "middle", horizontal: "left" };

  // Title + gold underline strip
  sheet.mergeCells(4, 1, 4, colCount);
  const titleRow = sheet.getRow(4);
  titleRow.height = 24;
  titleRow.getCell(1).value = "Convert History";
  titleRow.getCell(1).font = {
    name: "Calibri",
    size: 14,
    bold: true,
    color: { argb: `FF${INK}` },
  };
  titleRow.getCell(1).alignment = { vertical: "middle", horizontal: "left" };
  titleRow.getCell(1).border = {
    bottom: { style: "medium", color: { argb: `FF${GOLD}` } },
  };

  // Meta chips row — three readable blocks
  sheet.mergeCells(5, 1, 5, 3);
  sheet.mergeCells(5, 4, 5, 6);
  sheet.mergeCells(5, 7, 5, 8);
  const metaRow = sheet.getRow(5);
  metaRow.height = 20;
  const metaCells = [
    { col: 1, text: `Exported  ${exportedAt}` },
    { col: 4, text: `Time  ${timeLabel}   ·   Status  ${statusLabel}` },
    { col: 7, text: `Records  ${list.length}` },
  ];
  metaCells.forEach(({ col, text }) => {
    const cell = metaRow.getCell(col);
    cell.value = text;
    cell.font = { name: "Calibri", size: 10, color: { argb: `FF${MUTED}` } };
    cell.alignment = { vertical: "middle", horizontal: "left" };
  });

  // Soft secondary band under meta
  paintRow(sheet, 6, colCount, GOLD_BAND);
  sheet.mergeCells(6, 1, 6, colCount);
  const noteRow = sheet.getRow(6);
  noteRow.height = 18;
  noteRow.getCell(1).value = "Official statement export · Spot wallet convert trades";
  noteRow.getCell(1).font = {
    name: "Calibri",
    size: 9,
    italic: true,
    color: { argb: `FF${INK}` },
  };
  noteRow.getCell(1).alignment = { vertical: "middle", horizontal: "left" };

  // Spacer
  sheet.getRow(7).height = 8;
  paintRow(sheet, 7, colCount, ROW_WHITE);

  // —— Table header ——
  const headerRowIndex = 8;
  const headers = ["Date", "Side", "You spend", "You receive", "Fee", "Wallet", "Status", "Reference"];
  const headerAlign = ["left", "center", "right", "right", "right", "center", "center", "left"];
  const headerRow = sheet.getRow(headerRowIndex);
  headerRow.height = 24;
  headers.forEach((label, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = label;
    cell.font = { name: "Calibri", size: 11, bold: true, color: { argb: `FF${HEADER_FG}` } };
    cell.fill = solidFill(HEADER_BG);
    cell.alignment = { vertical: "middle", horizontal: headerAlign[i] };
    cell.border = {
      top: { style: "thin", color: { argb: `FF${HEADER_BG}` } },
      left: { style: "thin", color: { argb: `FF${HEADER_BG}` } },
      bottom: { style: "medium", color: { argb: `FF${GOLD}` } },
      right: { style: "thin", color: { argb: `FF${HEADER_BG}` } },
    };
  });

  if (list.length) {
    sheet.autoFilter = {
      from: { row: headerRowIndex, column: 1 },
      to: { row: headerRowIndex + list.length, column: colCount },
    };
  }

  // —— Data rows ——
  list.forEach((row, idx) => {
    const excelRow = sheet.getRow(headerRowIndex + 1 + idx);
    excelRow.height = 22;
    const values = [
      row.dateLabel || "—",
      row.sideLabel || "—",
      row.spendDisplay || "—",
      row.receiveDisplay || "—",
      row.feeDisplay || "—",
      row.wallet || "—",
      row.statusLabel || "—",
      row.id || "",
    ];
    const zebra = idx % 2 === 1 ? ALT_ROW : ROW_WHITE;
    const side = sideTone(row);
    const status = statusTone(row);

    values.forEach((val, i) => {
      const cell = excelRow.getCell(i + 1);
      cell.value = val;
      cell.font = { name: "Calibri", size: 10, color: { argb: `FF${INK}` } };
      cell.alignment = {
        vertical: "middle",
        horizontal: headerAlign[i],
        wrapText: i === 7,
      };
      cell.border = thinBorder();
      cell.fill = solidFill(zebra);

      // Side badge
      if (i === 1) {
        cell.font = {
          name: "Calibri",
          size: 10,
          bold: true,
          color: { argb: `FF${side.fg}` },
        };
        if (side.bg) cell.fill = solidFill(side.bg);
      }

      // Status badge
      if (i === 6) {
        cell.font = {
          name: "Calibri",
          size: 10,
          bold: true,
          color: { argb: `FF${status.fg}` },
        };
        if (status.bg) cell.fill = solidFill(status.bg);
      }

      // Money columns slightly heavier
      if (i === 2 || i === 3 || i === 4) {
        cell.font = { name: "Calibri", size: 10, bold: true, color: { argb: `FF${INK}` } };
      }
    });
  });

  // —— Footer ——
  const afterData = headerRowIndex + list.length + 1;
  sheet.getRow(afterData).height = 8;

  const footerRow = afterData + 1;
  sheet.mergeCells(footerRow, 1, footerRow, colCount);
  const footer = sheet.getRow(footerRow);
  footer.height = 20;
  paintRow(sheet, footerRow, colCount, GOLD_SOFT);
  footer.getCell(1).value = `© ${new Date().getFullYear()} ${BRAND_NAME} (${PLATFORM_NAME}). Confidential — for account holder use only.`;
  footer.getCell(1).font = {
    name: "Calibri",
    size: 9,
    italic: true,
    color: { argb: `FF${MUTED}` },
  };
  footer.getCell(1).alignment = { vertical: "middle", horizontal: "left" };

  const accentBottom = footerRow + 1;
  const bottomBar = sheet.getRow(accentBottom);
  bottomBar.height = 8;
  paintRow(sheet, accentBottom, colCount, GOLD);

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${PLATFORM_NAME}_Convert_History_${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 200);
}
